import type { LucideIcon } from "lucide-react";
import {
  FileText,
  HelpCircle,
  Image,
  MapPin,
  Mic,
  MousePointerClick,
  Sticker,
  Type,
  UserRound,
  Users,
  Video,
} from "lucide-react";
import type {
  MessageTypeName,
  PlatformTypeName,
  ReportMessageListItem,
  ReportMessageSummaryResponse,
} from "@multi-chat/shared";

export type IncomingReportMetricKey = "total" | PlatformTypeName;

export type IncomingReportMetricCard = {
  key: IncomingReportMetricKey;
  platform: PlatformTypeName | null;
  labelKey: string;
  /** Used only for the Total card; platform cards render ChannelIdentityBadge in the summary UI. */
  icon: LucideIcon;
};

export const INCOMING_REPORT_METRIC_CARDS: readonly IncomingReportMetricCard[] = [
  {
    key: "total",
    platform: null,
    labelKey: "reports.incoming.metrics.total",
    icon: Users,
  },
  {
    key: "WHATSAPP",
    platform: "WHATSAPP",
    labelKey: "reports.incoming.platforms.WHATSAPP",
    icon: Users,
  },
  {
    key: "FACEBOOK",
    platform: "FACEBOOK",
    labelKey: "reports.incoming.platforms.FACEBOOK",
    icon: Users,
  },
  {
    key: "INSTAGRAM",
    platform: "INSTAGRAM",
    labelKey: "reports.incoming.platforms.INSTAGRAM",
    icon: Users,
  },
];

export function incomingMetricCount(
  summary: ReportMessageSummaryResponse,
  key: IncomingReportMetricKey,
): number {
  if (key === "total") return summary.total;
  return summary.byPlatform.find((row) => row.platform === key)?.count ?? 0;
}

export function messageTypeLabelKey(messageType: MessageTypeName): string {
  switch (messageType) {
    case "TEXT":
      return "reports.incoming.messageTypes.TEXT";
    case "IMAGE":
      return "reports.incoming.messageTypes.IMAGE";
    case "VIDEO":
      return "reports.incoming.messageTypes.VIDEO";
    case "AUDIO":
      return "reports.incoming.messageTypes.AUDIO";
    case "DOCUMENT":
      return "reports.incoming.messageTypes.DOCUMENT";
    case "LOCATION":
      return "reports.incoming.messageTypes.LOCATION";
    case "CONTACT":
      return "reports.incoming.messageTypes.CONTACT";
    case "STICKER":
      return "reports.incoming.messageTypes.STICKER";
    case "INTERACTIVE":
      return "reports.incoming.messageTypes.INTERACTIVE";
    case "UNKNOWN":
      return "reports.incoming.messageTypes.UNKNOWN";
    default: {
      const _exhaustive: never = messageType;
      return _exhaustive;
    }
  }
}

export function messageTypeIcon(messageType: MessageTypeName): LucideIcon {
  switch (messageType) {
    case "TEXT":
      return Type;
    case "IMAGE":
      return Image;
    case "VIDEO":
      return Video;
    case "AUDIO":
      return Mic;
    case "DOCUMENT":
      return FileText;
    case "LOCATION":
      return MapPin;
    case "CONTACT":
      return UserRound;
    case "STICKER":
      return Sticker;
    case "INTERACTIVE":
      return MousePointerClick;
    case "UNKNOWN":
      return HelpCircle;
    default: {
      const _exhaustive: never = messageType;
      return _exhaustive;
    }
  }
}

/** Soft chip tones for incoming message-type badges. */
export function messageTypeChipClass(messageType: MessageTypeName): string {
  switch (messageType) {
    case "TEXT":
      return "border-line/70 bg-surface-muted text-muted";
    case "IMAGE":
    case "STICKER":
      return "border-accent/25 bg-accent/10 text-accent";
    case "VIDEO":
      return "border-accent/30 bg-accent/8 text-accent";
    case "AUDIO":
      return "border-warning/30 bg-warning-muted text-warning";
    case "DOCUMENT":
      return "border-line/70 bg-surface-muted text-foreground";
    case "LOCATION":
      return "border-success/25 bg-success-muted text-success";
    case "CONTACT":
      return "border-accent/20 bg-accent/8 text-accent";
    case "INTERACTIVE":
      return "border-line/70 bg-surface text-foreground";
    case "UNKNOWN":
      return "border-line/60 bg-surface-muted text-ink-muted";
    default: {
      const _exhaustive: never = messageType;
      return _exhaustive;
    }
  }
}

/** Compact inbound preview: media label + caption/filename, else text preview. */
export function incomingMessagePreview(
  t: (key: string, options?: Record<string, unknown>) => string,
  row: ReportMessageListItem,
): string {
  const typeLabel = t(messageTypeLabelKey(row.messageType));
  const caption = row.preview?.trim() ?? "";
  const fileName = row.attachment?.fileName?.trim() ?? "";

  if (row.messageType === "TEXT" || row.messageType === "UNKNOWN") {
    return caption.length > 0 ? caption : typeLabel;
  }

  if (fileName.length > 0) {
    return `${typeLabel} · ${fileName}`;
  }
  if (caption.length > 0) {
    return `${typeLabel} · ${caption}`;
  }
  return typeLabel;
}

export function endpointSecondaryLabel(
  endpoint: ReportMessageListItem["endpoint"],
  optionsSecondary?: string | null,
): string | null {
  if (optionsSecondary && optionsSecondary.trim().length > 0) {
    return optionsSecondary.trim();
  }
  const external = endpoint.externalEndpointId?.trim() || "";
  const label = endpoint.displayLabel?.trim() || "";
  if (external.length > 0 && external !== label) return external;
  return null;
}
